/**
 * SQL 검증 — SQL실행 노드의 스크립트를 **실행하지 않고** DB 에 해석만 시킨다 (backend services/sql_check).
 *
 * 문장마다 결과(통과·오류·경고·건너뜀)와 원문 줄 번호가 온다. DDL 은 해석만 해도 실행되는 DB 가 있어 검증하지 않는다.
 * 검증할 때마다 서버가 기록을 남긴다(워크플로별) — 편집기의 「검증 기록」 탭이 읽는다.
 *
 * 노드에는 마지막 검증의 요약 `cfg.sqlCheck = {hash, status, at, error, warn}` 를 둔다. hash 는 연결+SQL 이라
 * SQL 을 고치면 맞지 않게 되고, 그때 저장하면 검증할지 묻는다. 「검증 없이」 를 고르면 status 'skipped' 로 남긴다.
 */
import { be1Fetch } from '../config/api';
import { errorFromResponse } from './errorText';

/** 밖에서(저장 때 「고치기」) 노드의 SQL 편집기를 검증 결과와 함께 연다 — detail: {nodeId, pane} */
export const OPEN_SQL_CHECK_EVENT = 'openflow:open-sql-check';

export type StmtStatus ='ok' | 'error' | 'warn' | 'skipped';
export type CheckStatus = StmtStatus | 'empty' | 'failed';
export type CheckTrigger = 'manual' | 'apply' | 'save';

export interface StmtResult {
  index: number;
  /** 원문에서 문장이 시작·끝나는 줄 (1부터) */
  line: number;
  endLine: number;
  /** 오류가 난 줄 (DB 가 위치를 알려 줄 때만) */
  errorLine?: number;
  kind: 'query' | 'dml' | 'plsql' | 'call' | 'ddl' | 'tx' | 'other';
  text: string;
  status: StmtStatus;
  /** 건너뜀·경고의 사유 코드 — 화면 문구는 property:sqlCheck.reason.<code> */
  code?: string;
  params?: Record<string, string>;
  /** DB 가 돌려준 오류 원문 */
  message?: string;
  method?: string;
}

export interface CheckSummary { total: number; ok: number; error: number; warn: number; skipped: number }

export interface CheckResult {
  id: string;
  at: string;
  dbType: string;
  method: string;
  statements: StmtResult[];
  summary: CheckSummary;
  status: CheckStatus;
}

export interface CheckLogEntry extends Partial<CheckResult> {
  id: string;
  at: string;
  status: CheckStatus;
  user?: string;
  connection?: string;
  trigger?: CheckTrigger;
  hash?: string;
  nodeId?: string;
  nodeLabel?: string;
  message?: string;
}

/** 노드에 남기는 마지막 검증 요약 */
export interface NodeSqlCheck { hash: string; status: CheckStatus; at: string; error?: number; warn?: number }

export interface CheckRequest {
  connection: string;
  sql: string;
  params?: Array<{ name: string; value: string }>;
  workflowId?: string | null;
  nodeId?: string | null;
  nodeLabel?: string | null;
  trigger: CheckTrigger;
}

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) throw await errorFromResponse(res);
  return res.json();
}

export async function runSqlCheck(req: CheckRequest): Promise<CheckResult> {
  return json(await be1Fetch('/api/sql-check', {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...req, params: req.params || [], hash: sqlHash(req.connection, req.sql) }),
  }));
}

export async function loadSqlCheckLog(workflowId: string | null | undefined, nodeId?: string | null, limit = 30): Promise<CheckLogEntry[]> {
  const qs = new URLSearchParams();
  if (workflowId) qs.set('workflowId', workflowId);
  if (nodeId) qs.set('nodeId', nodeId);
  qs.set('limit', String(limit));
  return (await json<{ items: CheckLogEntry[] }>(await be1Fetch(`/api/sql-check/log?${qs}`, { credentials: 'include' }))).items;
}

/** 연결+SQL 지문 — 마지막 검증 이후 바뀌었는지 본다 (줄끝 차이는 같은 것으로) */
export function sqlHash(connection: string | undefined, sql: string | undefined): string {
  const s = `${connection || ''}\n${(sql || '').replace(/\r\n/g, '\n').trim()}`;
  let h1 = 0x811c9dc5, h2 = 5381;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0;
    h2 = (Math.imul(h2, 33) + c) >>> 0;
  }
  return `${h1.toString(16)}${h2.toString(16)}`;
}

/** 검증 결과 → 노드에 남길 요약 */
export function toNodeCheck(connection: string, sql: string, r: Pick<CheckResult, 'status' | 'at' | 'summary'>): NodeSqlCheck {
  return { hash: sqlHash(connection, sql), status: r.status, at: r.at, error: r.summary?.error || 0, warn: r.summary?.warn || 0 };
}

/** 「검증 없이」 저장·적용 — 같은 SQL 로 다시 묻지 않게 그 선택을 남긴다 */
export function skippedCheck(connection: string, sql: string): NodeSqlCheck {
  return { hash: sqlHash(connection, sql), status: 'skipped', at: new Date().toISOString() };
}

/** 이 노드의 SQL 이 마지막 검증(또는 건너뛰기 선택) 이후 바뀌었나 */
export function needsCheck(cfg: { connection?: string; sql?: string; sqlCheck?: NodeSqlCheck } | undefined): boolean {
  if (!cfg || !(cfg.sql || '').trim() || !cfg.connection) return false;
  return cfg.sqlCheck?.hash !== sqlHash(cfg.connection, cfg.sql);
}

/** 저장 스냅샷과 견주어 SQL·연결이 바뀐 SQL실행 노드 — 아직 검증하지 않은 것만 */
export function changedSqlNodes(snapshot: string | undefined, nodes: any[]): any[] {
  let saved: Record<string, string> = {};
  try {
    const doc = snapshot ? JSON.parse(snapshot) : { nodes: [] };
    saved = Object.fromEntries((doc.nodes || []).filter((n: any) => n.type === 'sqlScript')
      .map((n: any) => [n.id, sqlHash(n.data?.config?.connection, n.data?.config?.sql)]));
  } catch { /* 스냅샷을 못 읽으면 모두 새것으로 본다 */ }
  return (nodes || []).filter((n) => n.type === 'sqlScript').filter((n) => {
    const cfg = n.data?.config || {};
    return needsCheck(cfg) && saved[n.id] !== sqlHash(cfg.connection, cfg.sql);
  });
}

/** 노드마다 마지막 결과 — 편집기를 닫았다 열거나, 저장 때 검증한 결과를 편집기에서 다시 볼 때 (앱이 떠 있는 동안만) */
const lastResults = new Map<string, { hash: string; result: CheckResult }>();
export function rememberResult(nodeId: string, connection: string, sql: string, result: CheckResult): void {
  lastResults.set(nodeId, { hash: sqlHash(connection, sql), result });
}
export function recallResult(nodeId: string): { hash: string; result: CheckResult } | undefined {
  return lastResults.get(nodeId);
}

/** 결과를 원문 줄에 표시할 때 — 줄(1부터) → 가장 나쁜 상태 */
export function lineMarks(stmts: StmtResult[]): Map<number, StmtStatus> {
  const rank: Record<StmtStatus, number> = { ok: 0, skipped: 1, warn: 2, error: 3 };
  const out = new Map<number, StmtStatus>();
  for (const s of stmts) {
    const lines = s.errorLine && s.errorLine !== s.line ? [s.line, s.errorLine] : [s.line];
    for (const l of lines) {
      const prev = out.get(l);
      if (!prev || rank[s.status] > rank[prev]) out.set(l, s.status);
    }
  }
  return out;
}
